import React, { useEffect, useState } from "react";
import axios from "axios";
import { Modal, Spinner, Dropdown, Badge } from "react-bootstrap";
import Swal from "sweetalert2";
import { BASE_URL } from "../../services/Api";
import { authHeader } from "../../services/auth-header";
import {
  FaEdit,
  FaEye,
  FaTrash,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaPlus,
  FaSearch,
  FaFilter,
  FaChevronLeft,
  FaChevronRight,
  FaEllipsisH,
  FaSave,
  FaUser,
  FaCalendarAlt,
  FaClock,
  FaSyncAlt
} from "react-icons/fa";
import "../Brand/BrandComponents.css";

const DepartmentsPage = () => {
  const [departments, setDepartments] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(5);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const [sortBy, setSortBy] = useState("id");
  const [sortDir, setSortDir] = useState("desc");

  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [formData, setFormData] = useState({ name: "" });

  const [saving, setSaving] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const pageSizeOptions = [5, 10, 20, 50];

  const avatarColors = ["#14b8a6", "#0d9488", "#3b82f6", "#f59e0b", "#ef4444", "#8b5cf6", "#64748b"];
  const getAvatarColor = (index) => avatarColors[index % avatarColors.length];
  const getInitials = (name) => name ? name.charAt(0).toUpperCase() : "D";

  const capitalizeAllWords = (text) => {
    if (!text) return text;
    return text.split(" ").map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" ");
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-IN");
  };

  const fetchDepartments = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${BASE_URL}department/page?pageNumber=${page}&pageSize=${size}&field=${sortBy}&direction=${sortDir}&search=${search}`,
        { headers: authHeader() }
      );
      const responseData = res.data;
      setDepartments(responseData.content || []);
      setTotalPages(responseData.totalPages || 1);
      setTotalRecords(responseData.totalElements || 0);
    } catch (error) {
      Swal.fire({ icon: "error", title: "Error", text: "Failed to fetch departments" });
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => { fetchDepartments(); }, [page, size, search, sortBy, sortDir]);

  const refreshHandler = () => {
    setIsRefreshing(true);
    fetchDepartments();
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: capitalizeAllWords(e.target.value) });
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      Swal.fire({ icon: "warning", title: "Validation Error", text: "Please enter department name" });
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${BASE_URL}department/`, formData, { headers: authHeader() });
      Swal.fire({ icon: "success", title: "Success", text: "Department added successfully!" });
      setShowModal(false);
      setFormData({ name: "" });
      fetchDepartments();
    } catch (error) {
      Swal.fire({ icon: "error", title: "Error", text: error.response?.data?.message || "Something went wrong" });
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!formData.name.trim()) {
      Swal.fire({ icon: "warning", title: "Validation Error", text: "Please enter department name" });
      return;
    }
    setUpdating(true);
    try {
      await axios.put(`${BASE_URL}department/${selectedDepartment.id}`, formData, { headers: authHeader() });
      Swal.fire({ icon: "success", title: "Success", text: "Department updated successfully!" });
      setShowModal(false);
      setFormData({ name: "" });
      setSelectedDepartment(null);
      fetchDepartments();
    } catch (error) {
      Swal.fire({ icon: "error", title: "Error", text: error.response?.data?.message || "Failed to update department" });
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${BASE_URL}department/${selectedDepartment.id}`, { headers: authHeader() });
      Swal.fire({ icon: "success", title: "Deleted!", text: "Department deleted successfully." });
      setShowDeleteModal(false);
      setSelectedDepartment(null);
      fetchDepartments();
    } catch (error) {
      Swal.fire({ icon: "error", title: "Error", text: "Failed to delete department" });
    } finally {
      setDeleting(false);
    }
  };

  const handleSort = (column) => {
    setSortBy(column);
    setSortDir(sortBy === column ? (sortDir === "asc" ? "desc" : "asc") : "desc");
    setPage(0);
  };

  const handleAddNew = () => { setFormData({ name: "" }); setSelectedDepartment(null); setShowModal(true); };
  const handleEdit = (dept) => { setSelectedDepartment(dept); setFormData({ name: dept.name }); setShowModal(true); };
  const handleView = (dept) => { setSelectedDepartment(dept); setShowViewModal(true); };
  const handleConfirmDelete = (dept) => { setSelectedDepartment(dept); setShowDeleteModal(true); };

  const getSortIcon = (column) => {
    if (sortBy !== column) return <FaSort style={{ marginLeft: 4, opacity: 0.4 }} size={11} />;
    return sortDir === "asc"
      ? <FaSortUp style={{ marginLeft: 4 }} size={11} />
      : <FaSortDown style={{ marginLeft: 4 }} size={11} />;
  };

  const handlePageChange = (newPage) => setPage(newPage);
  const handlePrevious = () => setPage(p => Math.max(0, p - 1));
  const handleNext = () => setPage(p => Math.min(totalPages - 1, p + 1));
  const handleSizeChange = (e) => { setSize(parseInt(e.target.value)); setPage(0); };

  const renderPaginationNumbers = () => {
    const pageNums = [];
    const maxVisible = 5;
    const startPage = Math.max(0, page - Math.floor(maxVisible / 2));
    const endPage = Math.min(totalPages - 1, startPage + maxVisible - 1);

    if (startPage > 0) {
      pageNums.push(
        <button key={0} className="bc-page-btn" onClick={() => handlePageChange(0)}>1</button>
      );
      if (startPage > 1) pageNums.push(<span key="dots1" className="bc-page-dots">...</span>);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNums.push(
        <button
          key={i}
          className={`bc-page-btn${page === i ? " bc-page-btn--active" : ""}`}
          onClick={() => handlePageChange(i)}
        >
          {i + 1}
        </button>
      );
    }

    if (endPage < totalPages - 1) {
      if (endPage < totalPages - 2) pageNums.push(<span key="dots2" className="bc-page-dots">...</span>);
      pageNums.push(
        <button key={totalPages - 1} className="bc-page-btn" onClick={() => handlePageChange(totalPages - 1)}>
          {totalPages}
        </button>
      );
    }
    return pageNums;
  };

  /* ─────────────────────────────────────────── */
  return (
    <div style={{ padding: "1rem" }}>

      {/* ── CARD ── */}
      <div className="bc-card">

        {/* TOOLBAR - New unified toolbar */}
        <div className="bc-toolbar">
          {/* Left: Search with icon */}
          <div style={{ position: "relative", flex: 1, minWidth: 200 }}>
            <input
              className="bc-search"
              style={{ width: "100%", paddingLeft: "2rem" }}
              type="text"
              placeholder="Search by department name..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            />
            <FaSearch
              size={12}
              style={{ position: "absolute", left: "0.75rem", top: "50%", transform: "translateY(-50%)", color: "var(--bc-gray-400)", pointerEvents: "none" }}
            />
          </div>

          {/* Right: Show entries + Filter + Refresh + Add */}
          <div className="bc-toolbar__right">
            <span className="bc-entries__label">Show:</span>
            <select className="bc-entries__select" value={size} onChange={handleSizeChange}>
              {pageSizeOptions.map(o => <option key={o} value={o}>{o}</option>)}
            </select>

            {/* Refresh Button */}
            <button
              className={`bc-refresh-btn${isRefreshing ? ' bc-refresh-btn--spinning' : ''}`}
              onClick={refreshHandler}
              title="Refresh"
            >
              <FaSyncAlt size={12} />
            </button>

            {/* Add Button */}
            <button className="bc-add-btn" onClick={handleAddNew}>
              <FaPlus size={11} />
              Add Department
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bc-table-wrapper">
          <table className="bc-table">
            <thead>
              <tr>
                <th className="bc-th bc-th--sortable" style={{ width: 70 }} onClick={() => handleSort("id")}>
                  ID {getSortIcon("id")}
                </th>
                <th className="bc-th bc-th--sortable" style={{ minWidth: 200 }} onClick={() => handleSort("name")}>
                  Department {getSortIcon("name")}
                </th>
                <th className="bc-th" style={{ minWidth: 150 }}>Created Info</th>
                <th className="bc-th" style={{ minWidth: 150 }}>Updated Info</th>
                <th className="bc-th" style={{ minWidth: 130, textAlign: "center" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr className="bc-tr">
                  <td colSpan={5} className="bc-td" style={{ textAlign: "center", padding: "var(--space-6)" }}>
                    <Spinner animation="border" variant="primary" size="sm" className="me-2" />
                    <span style={{ color: "var(--bc-gray-500)" }}>Loading departments...</span>
                  </td>
                </tr>
              ) : departments.length > 0 ? (
                departments.map((dept, index) => (
                  <tr key={dept.id} className="bc-tr">

                    {/* ID */}
                    <td className="bc-td">
                      <span className="bc-cell-text bc-cell-text--id">{dept.id}</span>
                    </td>

                    {/* Department name with avatar */}
                    <td className="bc-td">
                      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        <div style={{
                          width: 32, height: 32, borderRadius: "50%",
                          background: getAvatarColor(index),
                          display: "flex", alignItems: "center", justifyContent: "center",
                          color: "#fff", fontSize: "0.8rem", fontWeight: 700, flexShrink: 0
                        }}>
                          {getInitials(dept.name)}
                        </div>
                        <div>
                          <div style={{ fontWeight: 600, color: "var(--bc-gray-800)" }}>{dept.name}</div>
                          <small style={{ color: "var(--bc-gray-400)" }}>Department</small>
                        </div>
                      </div>
                    </td>

                    {/* Created info */}
                    <td className="bc-td">
                      <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 4 }}>
                        <FaCalendarAlt size={10} style={{ color: "var(--bc-gray-400)" }} />
                        <span style={{ color: "var(--bc-gray-500)" }}>Date:</span>
                        <span style={{ fontWeight: 600, color: "var(--bc-gray-700)", marginLeft: 2 }}>{formatDate(dept.createddate)}</span>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <FaUser size={10} style={{ color: "var(--bc-gray-400)" }} />
                        <span style={{ color: "var(--bc-gray-500)" }}>By:</span>
                        <span style={{ fontWeight: 600, color: "var(--bc-gray-700)", marginLeft: 2 }}>{dept.createbyname || "N/A"}</span>
                      </div>
                    </td>

                    {/* Updated info */}
                    <td className="bc-td">
                      <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 4 }}>
                        <FaCalendarAlt size={10} style={{ color: "var(--bc-gray-400)" }} />
                        <span style={{ color: "var(--bc-gray-500)" }}>Date:</span>
                        <span style={{ fontWeight: 600, color: "var(--bc-gray-700)", marginLeft: 2 }}>{formatDate(dept.updateddate)}</span>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <FaUser size={10} style={{ color: "var(--bc-gray-400)" }} />
                        <span style={{ color: "var(--bc-gray-500)" }}>By:</span>
                        <span style={{ fontWeight: 600, color: "var(--bc-gray-700)", marginLeft: 2 }}>{dept.updatedbyname || "N/A"}</span>
                        {dept.updatedtime && (
                          <>
                            <FaClock size={8} style={{ color: "var(--bc-gray-400)", marginLeft: 6 }} />
                            <span style={{ color: "var(--bc-gray-400)" }}>{dept.updatedtime}</span>
                          </>
                        )}
                      </div>
                    </td>

                    {/* Actions */}
                    <td className="bc-td">
                      <div className="bc-actions" style={{ justifyContent: "center" }}>
                        <button className="bc-action-btn bc-action-btn--view" onClick={() => handleView(dept)} title="View">
                          <FaEye size={13} />
                        </button>
                        <button className="bc-action-btn bc-action-btn--edit" onClick={() => handleEdit(dept)} title="Edit">
                          <FaEdit size={13} />
                        </button>
                        <button className="bc-action-btn bc-action-btn--delete" onClick={() => handleConfirmDelete(dept)} title="Delete">
                          <FaTrash  size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr className="bc-tr">
                  <td colSpan={5} className="bc-td bc-td--empty">
                    <FaSearch size={28} style={{ marginBottom: 8, opacity: 0.2, display: "block", margin: "0 auto 8px" }} />
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>No departments found</div>
                    <div>{search ? "Try adjusting your search terms" : "Get started by adding your first department"}</div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {departments.length > 0 && (
          <div className="bc-pagination">
            <span className="bc-pagination__info">
              Showing <strong>{page * size + 1}–{Math.min((page + 1) * size, totalRecords)}</strong> of <strong>{totalRecords}</strong> departments
            </span>
            <div className="bc-pagination__controls">
              <button
                className={`bc-nav-btn${page <= 0 ? " bc-nav-btn--disabled" : ""}`}
                onClick={handlePrevious}
                disabled={page <= 0}
              >
                <FaChevronLeft size={11} />
              </button>
              <div className="bc-pages">{renderPaginationNumbers()}</div>
              <button
                className={`bc-nav-btn${page >= totalPages - 1 ? " bc-nav-btn--disabled" : ""}`}
                onClick={handleNext}
                disabled={page >= totalPages - 1}
              >
                <FaChevronRight size={11} />
              </button>
            </div>
          </div>
        )}

      </div>

      {/* ── ADD / EDIT MODAL ── */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton style={{ background: "linear-gradient(135deg, var(--bc-teal-50), var(--bc-teal-100))", borderBottom: "1px solid var(--bc-teal-200)" }}>
          <Modal.Title style={{ fontSize: "13px", fontWeight: 700, color: "var(--bc-teal-800)" }}>
            {selectedDepartment ? "Edit Department" : "Add Department"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: "24px" }}>
          <div style={{ marginBottom: "20px" }}>
            <label className="form-label">Department Name</label>
            <input
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter department name"
              className="form-control"
            />
            <div style={{ fontSize: "11px", color: "var(--bc-gray-500)", marginTop: "8px" }}>
              Department name will be automatically capitalized
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer style={{ background: "var(--bc-gray-50)", borderTop: "1px solid var(--bc-gray-200)", padding: "16px 24px" }}>
          <button className="bc-add-btn" style={{ background: "var(--bc-white)", color: "var(--bc-teal-700)", border: "1px solid var(--bc-teal-300)", boxShadow: "none" }} onClick={() => setShowModal(false)}>
            Cancel
          </button>
          <button
            className="bc-add-btn"
            onClick={selectedDepartment ? handleUpdate : handleSave}
            disabled={saving || updating}
            style={{ opacity: saving || updating ? 0.7 : 1 }}
          >
            {saving || updating ? (
              <><Spinner animation="border" size="sm" className="me-2" />{selectedDepartment ? "Updating..." : "Saving..."}</>
            ) : (
              <><FaSave size={12} />{selectedDepartment ? "Update Department" : "Add Department"}</>
            )}
          </button>
        </Modal.Footer>
      </Modal>

      {/* ── VIEW MODAL ── */}
      <Modal show={showViewModal} onHide={() => setShowViewModal(false)} centered>
        <Modal.Header closeButton style={{ background: "linear-gradient(135deg, var(--bc-teal-50), var(--bc-teal-100))", borderBottom: "1px solid var(--bc-teal-200)" }}>
          <Modal.Title style={{ fontSize: "13px", fontWeight: 700, color: "var(--bc-teal-800)" }}>
            Department Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: "24px" }}>
          {selectedDepartment && (
            <div style={{ textAlign: "center" }}>
              <div style={{
                width: 80, height: 80, borderRadius: "50%",
                background: getAvatarColor(0),
                display: "flex", alignItems: "center", justifyContent: "center",
                color: "#fff", fontSize: "1.5rem", fontWeight: 700,
                margin: "0 auto 16px"
              }}>
                {getInitials(selectedDepartment.name)}
              </div>
              <h5 style={{ marginBottom: "8px", color: "var(--bc-teal-800)" }}>{selectedDepartment.name}</h5>
              <span className="status-badge status-active" style={{ marginBottom: "16px", display: "inline-block" }}>Department</span>

              <div style={{ textAlign: "left", marginTop: "16px" }}>
                {[
                  ["Department ID", selectedDepartment.id],
                  ["Name", selectedDepartment.name],
                  ["Created Date", formatDate(selectedDepartment.createddate)],
                  ["Created By", selectedDepartment.createbyname || "N/A"],
                  ...(selectedDepartment.updateddate ? [
                    ["Updated Date", formatDate(selectedDepartment.updateddate)],
                    ["Updated By", selectedDepartment.updatedbyname || "N/A"],
                    ...(selectedDepartment.updatedtime ? [["Updated Time", selectedDepartment.updatedtime]] : [])
                  ] : [])
                ].map(([label, val]) => (
                  <div key={label} style={{ marginBottom: "8px", fontSize: "12px" }}>
                    <strong style={{ color: "var(--bc-teal-800)" }}>{label}:</strong>
                    <span style={{ marginLeft: 6, color: "var(--bc-gray-700)" }}>{val}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer style={{ background: "var(--bc-gray-50)", borderTop: "1px solid var(--bc-gray-200)", padding: "16px 24px" }}>
          <button className="bc-add-btn" style={{ background: "var(--bc-white)", color: "var(--bc-teal-700)", border: "1px solid var(--bc-teal-300)", boxShadow: "none" }} onClick={() => setShowViewModal(false)}>
            Close
          </button>
        </Modal.Footer>
      </Modal>

      {/* ── DELETE CONFIRM MODAL ── */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton style={{ background: "linear-gradient(135deg, var(--bc-teal-50), var(--bc-teal-100))", borderBottom: "1px solid var(--bc-teal-200)" }}>
          <Modal.Title style={{ fontSize: "13px", fontWeight: 700, color: "var(--bc-teal-800)" }}>
            Delete Department
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: "24px", textAlign: "center" }}>
          {selectedDepartment && (
            <>
              <div style={{
                width: 50, height: 50, borderRadius: "50%",
                background: "var(--bc-error)", display: "flex", alignItems: "center", justifyContent: "center",
                color: "#fff", fontSize: "1rem", fontWeight: 700, margin: "0 auto 12px"
              }}>
                {getInitials(selectedDepartment.name)}
              </div>
              <h6 style={{ marginBottom: 4, color: "var(--bc-gray-800)" }}>{selectedDepartment.name}</h6>
              <small style={{ color: "var(--bc-gray-400)" }}>Department</small>
              <p style={{ marginTop: "16px", color: "var(--bc-gray-600)", fontSize: "12px" }}>
                Are you sure you want to delete <strong>{selectedDepartment.name}</strong>? This action cannot be undone.
              </p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer style={{ background: "var(--bc-gray-50)", borderTop: "1px solid var(--bc-gray-200)", padding: "16px 24px" }}>
          <button className="bc-add-btn" style={{ background: "var(--bc-white)", color: "var(--bc-teal-700)", border: "1px solid var(--bc-teal-300)", boxShadow: "none" }} onClick={() => setShowDeleteModal(false)}>
            Cancel
          </button>
          <button
            className="bc-add-btn"
            style={{ background: "linear-gradient(135deg, var(--bc-error), #dc2626)", opacity: deleting ? 0.7 : 1 }}
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? <><Spinner animation="border" size="sm" className="me-2" />Deleting...</> : "Delete Department"}
          </button>
        </Modal.Footer>
      </Modal>

    </div>
  );
};

export default DepartmentsPage;